import HiddenInput from "@/components/form/components/HiddenInput";
import { datePickerFormItemProps, datePickerStandardProps } from "@/utils/componentUtils";
import { formatPhoneNumber } from "@/utils/formatUtils";
import { phoneNumberNormalizeFunction } from "@/utils/formUtils";
import { isOtherContact, toDate } from "@/utils/utils";
import { validations } from "@/utils/validationUtils";
import { Col, DatePicker, Form, Input, Row, Select } from "antd";
import dayjs, { Dayjs } from "dayjs";
import { useTranslation } from "react-i18next";
import { rowGutter } from "../../constants";
import { ContactType, MarketingConsent } from "../../enums";
import { Contact } from "../../types";
import ActionButton from "../buttons/ActionButton";
import DeleteIcon from "../icons/DeleteIcon";
import Ellipsis from "../views/Ellipsis";
import { ContactTypeIcon } from "./ContactTypeIcon";
import MarketingConsentSelect from "./MarketingConsentSelect";

const smallColSpan = 3;
const colSpan = 4;

const getValueRules = (type?: ContactType) => {
  const rules = [validations.notBlank, validations.size(1, 255)];

  switch (type) {
    case ContactType.EMAIL:
    case ContactType.MAIN_EMAIL:
      rules.push(validations.email);
      break;
    case ContactType.PHONE_NUMBER:
    case ContactType.MAIN_PHONE_NUMBER:
      rules.push(validations.phoneNumber);
      break;
  }

  return rules;
};

interface Props {
  marketingConsentApplicable?: boolean;
  onMarketingConsentChange?: (consent: MarketingConsent, index: number) => void;
  onMarketingConsentStartDateChange?: (startDate: Dayjs, index: number) => void;
  hasMainContacts?: boolean;
}

export const ContactsForm = ({
  marketingConsentApplicable,
  onMarketingConsentChange,
  onMarketingConsentStartDateChange,
  hasMainContacts,
}: Props) => {
  const { t } = useTranslation();

  const form = Form.useFormInstance();
  const contacts = Form.useWatch("contacts", form) as Contact[] | undefined;

  return (
    <Form.List name={["contacts"]} initialValue={[]}>
      {(fields, { add, remove }) => (
        <>
          {fields.map(({ key, ...field }) => (
            <Row gutter={rowGutter} key={key}>
              {!marketingConsentApplicable && (
                <HiddenInput name={[field.name, "marketingConsent"]} initialValue={MarketingConsent.NOT_GRANTED} />
              )}

              <Col span={colSpan}>
                <Form.Item
                  {...field}
                  name={[field.name, "type"]}
                  label={<Ellipsis>{t("contact.enums.type._label")}</Ellipsis>}
                  rules={[validations.notNull]}
                >
                  <Select<ContactType>
                    options={Object.values(ContactType)
                      .filter(
                        (type) =>
                          hasMainContacts ||
                          (type !== ContactType.MAIN_EMAIL && type !== ContactType.MAIN_PHONE_NUMBER),
                      )
                      .map((type) => ({
                        value: type,
                        label: (
                          <>
                            <ContactTypeIcon type={type} /> {t("contact.enums.type." + type)}
                          </>
                        ),
                      }))}
                  />
                </Form.Item>
              </Col>

              <>
                <Col span={colSpan + 1}>
                  <Form.Item
                    {...field}
                    name={[field.name, "value"]}
                    label={t("contact.attrs.value")}
                    rules={getValueRules(contacts?.[field.name]?.type)}
                    getValueProps={(value) => ({
                      value:
                        value &&
                        (contacts?.[field.name]?.type === ContactType.PHONE_NUMBER ||
                          contacts?.[field.name]?.type === ContactType.MAIN_PHONE_NUMBER)
                          ? formatPhoneNumber(value)
                          : value,
                    })}
                    normalize={(value) =>
                      contacts?.[field.name]?.type === ContactType.PHONE_NUMBER ||
                      contacts?.[field.name]?.type === ContactType.MAIN_PHONE_NUMBER
                        ? phoneNumberNormalizeFunction(value)
                        : value
                    }
                  >
                    <Input />
                  </Form.Item>
                </Col>

                <Col span={colSpan}>
                  <Form.Item
                    {...field}
                    name={[field.name, "description"]}
                    label={t("contact.attrs.description")}
                    rules={[validations.size(1, 255)]}
                  >
                    <Input />
                  </Form.Item>
                </Col>

                {marketingConsentApplicable &&
                  contacts?.[field.name]?.type &&
                  !isOtherContact(contacts?.[field.name] as Contact) && (
                    <>
                      <Col span={colSpan}>
                        <MarketingConsentSelect
                          formItemProps={{
                            ...field,
                            name: [field.name, "marketingConsent"],
                            label: <Ellipsis>{t("contact.enums.marketingConsent._label")}</Ellipsis>,
                            rules: [validations.notNull],
                          }}
                          selectProps={{
                            onChange: (consent) => onMarketingConsentChange?.(consent, field.name),
                          }}
                        />
                      </Col>

                      <Col span={smallColSpan}>
                        <Form.Item
                          {...field}
                          name={[field.name, "marketingConsentStartDate"]}
                          label={t("contact.attrs.marketingConsentStartDate")}
                          rules={[validations.notNull]}
                          {...datePickerFormItemProps}
                        >
                          <DatePicker<Dayjs>
                            {...datePickerStandardProps}
                            disabled={
                              contacts?.[field.name]?.marketingConsent === MarketingConsent.NOT_GRANTED || undefined
                            }
                            onChange={(startDate) =>
                              startDate ? onMarketingConsentStartDateChange?.(startDate, field.name) : undefined
                            }
                          />
                        </Form.Item>
                      </Col>

                      <Col span={smallColSpan}>
                        <Form.Item
                          {...field}
                          name={[field.name, "marketingConsentEndDate"]}
                          label={t("contact.attrs.marketingConsentEndDate")}
                          rules={[
                            validations.notNull,
                            ...(contacts?.[field.name]?.marketingConsentStartDate
                              ? [
                                  validations.dateInInterval(
                                    dayjs(contacts?.[field.name]?.marketingConsentStartDate).add(1, "day"),
                                    dayjs(contacts?.[field.name]?.marketingConsentStartDate).add(5, "year"),
                                  ),
                                ]
                              : []),
                          ]}
                          {...datePickerFormItemProps}
                        >
                          <DatePicker
                            {...datePickerStandardProps}
                            disabled={
                              contacts?.[field.name]?.marketingConsent === MarketingConsent.NOT_GRANTED || undefined
                            }
                            minDate={toDate(contacts?.[field.name]?.marketingConsentStartDate)?.add(1, "day")}
                            maxDate={toDate(contacts?.[field.name]?.marketingConsentStartDate)?.add(5, "year")}
                          />
                        </Form.Item>
                      </Col>
                    </>
                  )}

                <Col
                  offset={
                    (marketingConsentApplicable &&
                      contacts?.[field.name]?.type &&
                      isOtherContact(contacts?.[field.name] as Contact)) ||
                    !contacts?.[field.name]?.type
                      ? 10
                      : undefined
                  }
                  span={1}
                >
                  <DeleteIcon onClick={() => remove(field.name)} />
                </Col>
              </>
            </Row>
          ))}

          <Row gutter={rowGutter}>
            <Col>
              <ActionButton
                className="margin-top-small"
                icon="plus"
                onClick={() =>
                  add({
                    type: undefined,
                    value: undefined,
                    description: undefined,
                    marketingConsent: undefined,
                    marketingConsentStartDate: undefined,
                    marketingConsentEndDate: undefined,
                  } as Record<string, any>)
                }
              >
                {t("contact.actions.add")}
              </ActionButton>
            </Col>
          </Row>
        </>
      )}
    </Form.List>
  );
};
